import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import jsQR from 'jsqr';
import {
  Truck,
  MapPin,
  Navigation,
  Phone,
  CheckCircle2,
  Play,
  Radio,
  Clock,
  ExternalLink,
  ShieldCheck,
  RefreshCw,
  QrCode,
  Camera,
  X as XIcon
} from 'lucide-react';
import { api } from '@/shared/api/axios';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Spinner } from '@/shared/components/ui/Spinner';
import { fetchRoute, type RouteResult } from '@/shared/utils/routing';

interface DriverProfile {
  id: string;
  name: string;
  phone: string;
  vehicleNumber: string;
  vehicleType: string;
  status: 'available' | 'on_trip' | 'off_duty';
  currentLocation?: {
    latitude: number;
    longitude: number;
    speed: number;
    battery: number;
    address: string;
    lastPingAt: string;
  };
}

interface ActiveDelivery {
  dispatchId: string;
  documentType: string;
  documentNumber: string;
  customerName: string;
  shippingAddress: string;
  contactPerson: string;
  phone: string;
  destinationLat?: number;
  destinationLng?: number;
  itemsSummary: string;
  dispatchedAt: string;
  startedAt?: string;
  status: 'assigned' | 'in_transit' | 'delivered';
  /** Set only on deliveries created after QR delivery confirmation shipped —
   *  '' on older/legacy deliveries, which need no scan to complete. */
  deliveryCode?: string;
}

interface ExpenseClaim {
  _id: string;
  amount: number;
  description: string;
  receiptUrl: string;
  expenseDate: string;
  status: 'pending' | 'reviewed';
  createdAt: string;
}

export function DriverPortalPage() {
  const [driver, setDriver] = useState<DriverProfile | null>(null);
  const [activeDelivery, setActiveDelivery] = useState<ActiveDelivery | null>(null);
  const [deliveryHistory, setDeliveryHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isGpsActive, setIsGpsActive] = useState(false);
  const [lastPingTime, setLastPingTime] = useState<string | null>(null);

  // Delivery Completion Modal State
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
  const [receiverName, setReceiverName] = useState('');
  const [receiverPhone, setReceiverPhone] = useState('');
  const [proofRemarks, setProofRemarks] = useState('');
  const [completing, setCompleting] = useState(false);

  // Field expense claims (client-requested: "proof of expense add karne ka
  // option ho, photo upload of the receipt"). A lightweight submission —
  // not a real accounting expense, which requires 'cashbook' access a
  // driver correctly doesn't have — that lands in a review queue for
  // Accounts (see src/api-fleet/index.js's /expense-claims routes).
  const [expenseClaims, setExpenseClaims] = useState<ExpenseClaim[]>([]);
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
  const [expenseAmount, setExpenseAmount] = useState('');
  const [expenseDescription, setExpenseDescription] = useState('');
  const [expenseReceiptUrl, setExpenseReceiptUrl] = useState('');
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [submittingExpense, setSubmittingExpense] = useState(false);
  const expenseReceiptFileRef = useRef<HTMLInputElement | null>(null);

  const fetchExpenseClaims = async () => {
    try {
      const res = await api.get<{ ok: boolean; claims: ExpenseClaim[] }>('/api/fleet/expense-claims');
      if (res?.data?.ok) setExpenseClaims(res.data.claims);
    } catch {
      // Non-critical background fetch — the submit form still works even
      // if the history list fails to load.
    }
  };

  // QR delivery confirmation (client-requested): the code sent to the
  // customer must be scanned back in — or typed, if the camera/scan fails —
  // before the delivery can be marked complete.
  const [scannedCode, setScannedCode] = useState('');
  const [manualCode, setManualCode] = useState('');
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannerError, setScannerError] = useState('');
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const scanCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const scanStreamRef = useRef<MediaStream | null>(null);
  const scanFrameRef = useRef<number | null>(null);

  const watchIdRef = useRef<number | null>(null);

  // Embedded route preview (client-requested: "Google Maps jaisa route
  // selection" for the driver's own delivery) — a real road route drawn
  // in-app before they leave, alongside (not instead of) the existing
  // "Open in Google Maps" link, which is still what handles actual
  // turn-by-turn navigation on the drive itself.
  const routeMapContainerRef = useRef<HTMLDivElement | null>(null);
  const routeMapInstanceRef = useRef<any>(null);
  const routeMapLayersRef = useRef<any[]>([]);
  const [routeInfo, setRouteInfo] = useState<RouteResult | null>(null);

  const fetchDriverDeliveries = async () => {
    try {
      setLoading(true);
      const res = await api.get<{
        ok: boolean;
        driver: DriverProfile;
        activeDelivery: ActiveDelivery | null;
        deliveryHistory: any[];
      }>('/api/fleet/my-deliveries');

      if (res?.data?.ok) {
        setDriver(res.data.driver);
        setActiveDelivery(res.data.activeDelivery);
        setDeliveryHistory(res.data.deliveryHistory || []);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to load driver deliveries.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDriverDeliveries();
    fetchExpenseClaims();
  }, []);

  // Embedded route preview map — initializes once the delivery card (and its
  // map container) is actually on screen, and re-draws whenever the
  // destination or the driver's last known position changes.
  useEffect(() => {
    const L = (window as any).L;
    const container = routeMapContainerRef.current;
    if (!L || !container) return;
    if (!activeDelivery?.destinationLat || !activeDelivery?.destinationLng) return;

    const originCoords = driver?.currentLocation?.latitude && driver?.currentLocation?.longitude
      ? { lat: driver.currentLocation.latitude, lng: driver.currentLocation.longitude }
      : { lat: 18.6279, lng: 73.8131 }; // Same depot default every other delivery preview in this codebase falls back to.
    const destCoords = { lat: activeDelivery.destinationLat, lng: activeDelivery.destinationLng };

    if (!routeMapInstanceRef.current) {
      routeMapInstanceRef.current = L.map(container, { zoomControl: false, attributionControl: false }).setView(
        [originCoords.lat, originCoords.lng],
        12,
      );
      L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}', {
        maxZoom: 19,
        attribution: 'Tiles &copy; Esri &mdash; Street Basemap',
      }).addTo(routeMapInstanceRef.current);
    }
    const map = routeMapInstanceRef.current;

    let cancelled = false;
    fetchRoute(originCoords, destCoords).then((route) => {
      if (cancelled) return;
      setRouteInfo(route);
      routeMapLayersRef.current.forEach((layer) => map.removeLayer(layer));
      routeMapLayersRef.current = [];

      const originMarker = L.circleMarker([originCoords.lat, originCoords.lng], {
        radius: 7, color: '#059669', fillColor: '#059669', fillOpacity: 1,
      }).addTo(map);
      const destMarker = L.marker([destCoords.lat, destCoords.lng]).addTo(map);
      const line = L.polyline(route.path, { color: '#2563eb', weight: 4, opacity: 0.85 }).addTo(map);
      routeMapLayersRef.current = [originMarker, destMarker, line];

      map.fitBounds(line.getBounds(), { padding: [24, 24] });
    });

    return () => { cancelled = true; };
  }, [activeDelivery?.destinationLat, activeDelivery?.destinationLng, driver?.currentLocation?.latitude, driver?.currentLocation?.longitude]);

  // HTML5 Geolocation Watcher for Continuous Live Telemetry
  const startLiveGpsTracking = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser.');
      return;
    }

    setIsGpsActive(true);
    toast.success('Live GPS Telemetry started! Location is streaming to ZYNX.');

    // Immediate ping
    navigator.geolocation.getCurrentPosition((pos) => {
      sendGpsPing(pos.coords.latitude, pos.coords.longitude, pos.coords.speed || 0);
    });

    // Continuous watch
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        sendGpsPing(pos.coords.latitude, pos.coords.longitude, pos.coords.speed || 0);
      },
      (err) => {
        console.warn('Geolocation watch error:', err.message);
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
    );
  };

  const stopLiveGpsTracking = () => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setIsGpsActive(false);
    toast('Live GPS broadcast paused.', { icon: '⏸️' });
  };

  const sendGpsPing = async (lat: number, lng: number, speed: number) => {
    setLastPingTime(new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));

    try {
      // 1. Ping fleet driver location
      await api.post('/api/fleet/ping-location', {
        latitude: lat,
        longitude: lng,
        speed: Math.round(speed * 3.6),
        battery: 95,
        address: 'Live On-Road Telemetry',
      });

      // 2. Also stream to presence engine for superadmin radar
      await api.post('/api/presence/location', {
        latitude: lat,
        longitude: lng,
        speed: Math.round(speed * 3.6),
        battery: 95,
        address: 'En Route to Delivery Site',
      });
    } catch (e) {
      // Silent background catch
    }
  };

  const handleStartTrip = async () => {
    try {
      const res = await api.post('/api/fleet/start-trip', {});
      if (res?.data?.ok) {
        toast.success('Trip started! Delivery status updated to In-Transit.');
        startLiveGpsTracking();
        fetchDriverDeliveries();
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to start trip.');
    }
  };

  const stopScanner = () => {
    if (scanFrameRef.current !== null) {
      cancelAnimationFrame(scanFrameRef.current);
      scanFrameRef.current = null;
    }
    scanStreamRef.current?.getTracks().forEach((track) => track.stop());
    scanStreamRef.current = null;
    setScannerOpen(false);
  };

  const startScanner = async () => {
    setScannerError('');
    setScannerOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      scanStreamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      const tick = () => {
        const video = videoRef.current;
        const canvas = scanCanvasRef.current;
        if (video && canvas && video.readyState === video.HAVE_ENOUGH_DATA) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const result = jsQR(imageData.data, imageData.width, imageData.height);
            if (result?.data) {
              setScannedCode(result.data.trim());
              toast.success('QR code scanned.');
              stopScanner();
              return;
            }
          }
        }
        scanFrameRef.current = requestAnimationFrame(tick);
      };
      scanFrameRef.current = requestAnimationFrame(tick);
    } catch (err: any) {
      setScannerError('Could not access the camera. Enter the code manually instead.');
      setScannerOpen(false);
    }
  };

  // Camera cleanup if the driver navigates away with the scanner still open.
  useEffect(() => () => stopScanner(), []);

  const handleCompleteDelivery = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!receiverName.trim()) {
      toast.error('Please enter the name of the person receiving the cargo.');
      return;
    }
    const confirmationCode = (scannedCode || manualCode).trim();
    if (activeDelivery?.deliveryCode && !confirmationCode) {
      toast.error("Scan the customer's QR code (or type the code) to confirm delivery.");
      return;
    }

    try {
      setCompleting(true);
      const res = await api.post('/api/fleet/complete-delivery', {
        receiverName: receiverName.trim(),
        receiverPhone: receiverPhone.trim(),
        proofRemarks: proofRemarks.trim() || 'Delivered in full at destination.',
        scannedCode: confirmationCode,
      });

      if (res?.data?.ok) {
        toast.success('🎉 Delivery completed & marked delivered in ZYNX!');
        setIsCompleteModalOpen(false);
        setScannedCode('');
        setManualCode('');
        stopLiveGpsTracking();
        fetchDriverDeliveries();
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to complete delivery.');
    } finally {
      setCompleting(false);
    }
  };

  const onExpenseReceiptFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingReceipt(true);
    const fd = new FormData();
    fd.append('file', file);
    api.post<{ url: string }>('/api/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((res) => {
        setExpenseReceiptUrl(res.data.url);
        toast.success('Receipt photo uploaded.');
      })
      .catch(() => toast.error('Receipt upload failed.'))
      .finally(() => setUploadingReceipt(false));
  };

  const handleSubmitExpense = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!(Number(expenseAmount) > 0)) {
      toast.error('Enter an amount greater than zero.');
      return;
    }
    try {
      setSubmittingExpense(true);
      await api.post('/api/fleet/expense-claims', {
        amount: Number(expenseAmount),
        description: expenseDescription.trim(),
        receiptUrl: expenseReceiptUrl,
      });
      toast.success('Expense claim submitted for review.');
      setIsExpenseModalOpen(false);
      setExpenseAmount('');
      setExpenseDescription('');
      setExpenseReceiptUrl('');
      fetchExpenseClaims();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to submit expense claim.');
    } finally {
      setSubmittingExpense(false);
    }
  };

  // Build Google Maps Navigation URL
  const destinationQuery = encodeURIComponent(
    activeDelivery?.shippingAddress || activeDelivery?.customerName || 'Pune'
  );
  const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${destinationQuery}`;

  return (
    <div className="min-h-screen bg-slate-100 p-3 sm:p-5 max-w-4xl mx-auto space-y-4">
      {/* Driver & Vehicle Top Card */}
      <div className="bg-success-text text-on-primary rounded-2xl p-4 sm:p-6 shadow-md">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-success border border-success/40 flex items-center justify-center text-2xl">
              🚚
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-lg sm:text-xl font-bold">{driver?.name || 'RBM Fleet Driver'}</h1>
                <span className="text-[10px] bg-success border border-success/40 text-success-text font-mono px-2 py-0.5 rounded-full uppercase">
                  {driver?.status || 'Active'}
                </span>
              </div>
              <p className="text-xs text-success-text font-mono mt-0.5">
                Truck: <span className="font-bold text-white">{driver?.vehicleNumber || 'MH 12 QW 4590'}</span> ({driver?.vehicleType || 'Tata 407'})
              </p>
            </div>
          </div>

          {/* GPS Broadcast Pill */}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            {isGpsActive ? (
              <button
                onClick={stopLiveGpsTracking}
                className="flex-1 sm:flex-none px-3 py-2 bg-success hover:bg-success border border-success/40 rounded-xl text-xs font-semibold flex items-center justify-center gap-2 text-success-text transition"
              >
                <Radio className="w-4 h-4 text-success-text animate-pulse" />
                <span>GPS Live ({lastPingTime || 'Broadcasting'})</span>
              </button>
            ) : (
              <button
                onClick={startLiveGpsTracking}
                className="flex-1 sm:flex-none px-3 py-2 bg-success-text hover:bg-success-text/90 rounded-lg text-xs font-semibold flex items-center justify-center gap-2 text-on-primary shadow-sm transition"
              >
                <Radio className="w-4 h-4" />
                <span>Start Live GPS Stream</span>
              </button>
            )}
            <button
              onClick={fetchDriverDeliveries}
              className="p-2 bg-success hover:bg-success text-success-text rounded-xl transition"
              title="Refresh deliveries"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Main Delivery Task Area */}
      {loading ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-slate-200 shadow-sm">
          <Spinner size="lg" />
          <p className="text-xs text-slate-500 mt-2 font-medium">Loading active delivery assignment...</p>
        </div>
      ) : activeDelivery ? (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden space-y-4 p-4 sm:p-6">
          {/* Header Banner */}
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <div className="flex items-center gap-2">
              <span className="p-2 rounded-lg bg-info/10 text-info-text">
                <Truck className="w-5 h-5" />
              </span>
              <div>
                <span className="text-[11px] font-bold text-info-text uppercase tracking-wider">
                  Active Delivery Trip
                </span>
                <h2 className="text-base font-bold text-slate-900 leading-tight">
                  {activeDelivery.documentNumber} — {activeDelivery.documentType.toUpperCase()}
                </h2>
              </div>
            </div>

            <span
              className={`text-xs font-bold px-2.5 py-1 rounded-full uppercase border ${
                activeDelivery.status === 'in_transit'
                  ? 'bg-info/10 text-info-text border-info/25 animate-pulse'
                  : 'bg-success/10 text-success-text border-success/25'
              }`}
            >
              {activeDelivery.status === 'in_transit' ? '● En Route' : '● Assigned'}
            </span>
          </div>

          {/* Customer & Destination Details */}
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-200/80 space-y-3">
            <div>
              <span className="text-[11px] font-bold text-slate-500 uppercase">Customer / Factory Destination</span>
              <p className="text-base font-bold text-slate-900">{activeDelivery.customerName}</p>
              <p className="text-xs text-slate-600 flex items-start gap-1.5 mt-1">
                <MapPin className="w-4 h-4 text-success-text shrink-0 mt-0.5" />
                <span>{activeDelivery.shippingAddress}</span>
              </p>
            </div>

            {/* Contact Person & Quick Call */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pt-3 border-t border-slate-200">
              <div>
                <span className="text-[11px] text-slate-500">Contact Person:</span>
                <p className="text-xs font-semibold text-slate-800">
                  {activeDelivery.contactPerson || 'Store Manager'} {activeDelivery.phone ? `(${activeDelivery.phone})` : ''}
                </p>
              </div>

              {activeDelivery.phone && (
                <a
                  href={`tel:${activeDelivery.phone}`}
                  className="py-2 px-3 bg-success-text hover:bg-success-text/90 text-on-primary rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 shadow-sm transition"
                >
                  <Phone className="w-3.5 h-3.5" /> Call Customer Contact
                </a>
              )}
            </div>

            {/* Cargo Summary */}
            <div className="pt-2 border-t border-slate-200">
              <span className="text-[11px] text-slate-500">Cargo Items:</span>
              <p className="text-xs font-bold text-success-text mt-0.5">
                📦 {activeDelivery.itemsSummary || 'Standard Lubricant Barrels'}
              </p>
            </div>
          </div>

          {/* Embedded Route Preview — the actual turn-by-turn drive still
              happens in Google Maps below; this is a quick "how far, which
              way" look before setting off. */}
          {activeDelivery.destinationLat && activeDelivery.destinationLng && (
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <div ref={routeMapContainerRef} className="w-full h-48" />
              <div className="px-3 py-1.5 bg-slate-50 border-t border-slate-200 text-[11px] font-bold text-slate-600 flex items-center justify-between">
                <span>Route preview</span>
                <span>{routeInfo ? `${routeInfo.distanceKm} km · ~${routeInfo.durationMin} min` : 'Routing…'}</span>
              </div>
            </div>
          )}

          {/* Interactive Navigation & Map Action */}
          <div className="p-4 bg-success/10 rounded-xl border border-success/25 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="p-2.5 bg-success-text text-on-primary rounded-lg">
                <Navigation className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-xs font-bold text-success-text">Live GPS Destination Map</h4>
                <p className="text-[11px] text-success-text">
                  Open turn-by-turn navigation in Google Maps directly on your phone.
                </p>
              </div>
            </div>

            <a
              href={googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto py-2.5 px-4 bg-white hover:bg-success/10 text-success-text border border-success/25 rounded-xl text-xs font-bold flex items-center justify-center gap-2 shadow-sm transition"
            >
              <ExternalLink className="w-4 h-4 text-success-text" />
              <span>Navigate in Google Maps</span>
            </a>
          </div>

          {/* Action Buttons: Start Trip / Complete Delivery */}
          <div className="pt-2">
            {activeDelivery.status === 'assigned' ? (
              <Button
                onClick={handleStartTrip}
                className="w-full py-3.5 bg-success-text hover:bg-success-text/90 text-on-primary rounded-xl text-sm font-bold flex items-center justify-center gap-2 shadow-md transition"
              >
                <Play className="w-4 h-4 fill-white" />
                <span>Start Delivery Trip &amp; Broadcast GPS</span>
              </Button>
            ) : (
              <Button
                onClick={() => setIsCompleteModalOpen(true)}
                className="w-full py-3.5 bg-success-text hover:bg-success-text/90 text-on-primary rounded-xl text-sm font-bold flex items-center justify-center gap-2 shadow-md transition"
              >
                <CheckCircle2 className="w-5 h-5" />
                <span>Mark Delivery as Completed</span>
              </Button>
            )}
          </div>
        </div>
      ) : (
        /* Empty State */
        <div className="bg-white rounded-2xl p-8 sm:p-12 text-center border border-slate-200 shadow-sm space-y-3">
          <div className="w-16 h-16 rounded-2xl bg-success/10 text-success-text flex items-center justify-center text-3xl mx-auto">
            ✅
          </div>
          <h2 className="text-base font-bold text-slate-900">All Deliveries Complete!</h2>
          <p className="text-xs text-slate-500 max-w-md mx-auto">
            You currently have no pending deliveries assigned. When the dispatch team assigns a new sample or product order, it will appear here immediately with route navigation.
          </p>
        </div>
      )}

      {/* Completed Delivery History */}
      <div className="bg-white rounded-2xl p-4 sm:p-6 border border-slate-200 shadow-sm space-y-3">
        <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
          <Clock className="w-4 h-4 text-slate-500" /> Recent Completed Deliveries
        </h3>

        {deliveryHistory.length === 0 ? (
          <p className="text-xs text-slate-400 py-3">No delivery history recorded yet.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {deliveryHistory.map((item, idx) => (
              <div key={idx} className="py-2.5 flex items-center justify-between text-xs">
                <div>
                  <p className="font-bold text-slate-900">{item.customerName}</p>
                  <p className="text-[11px] text-slate-500 font-mono">
                    {item.documentNumber} • Received by: {item.receiverName || 'Store In-Charge'}
                  </p>
                </div>
                <div className="text-right">
                  <span className="text-[10px] bg-success/10 text-success-text font-bold px-2 py-0.5 rounded-full">
                    Delivered
                  </span>
                  {item.completedAt && (
                    <p className="text-[10px] text-slate-400 mt-0.5">
                      {new Date(item.completedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Field Expense Claims */}
      <div className="bg-white rounded-2xl p-4 sm:p-6 border border-slate-200 shadow-sm space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
            <Camera className="w-4 h-4 text-slate-500" /> Expense Claims
          </h3>
          <Button size="sm" onClick={() => setIsExpenseModalOpen(true)}>
            + Submit Expense
          </Button>
        </div>

        {expenseClaims.length === 0 ? (
          <p className="text-xs text-slate-400 py-3">No expense claims submitted yet — fuel, toll, or other on-road costs.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {expenseClaims.map((claim) => (
              <div key={claim._id} className="py-2.5 flex items-center justify-between text-xs">
                <div>
                  <p className="font-bold text-slate-900">₹{claim.amount.toLocaleString('en-IN')}</p>
                  <p className="text-[11px] text-slate-500">{claim.description || 'No description'}</p>
                </div>
                <div className="text-right">
                  <span
                    className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      claim.status === 'reviewed'
                        ? 'bg-success/10 text-success-text'
                        : 'bg-warning/10 text-warning-text'
                    }`}
                  >
                    {claim.status === 'reviewed' ? 'Reviewed' : 'Pending Review'}
                  </span>
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    {new Date(claim.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Submit Expense Modal */}
      <Modal
        open={isExpenseModalOpen}
        onClose={() => setIsExpenseModalOpen(false)}
        title="Submit Expense Claim"
        size="md"
      >
        <form onSubmit={handleSubmitExpense} className="space-y-3.5">
          <Input
            type="number"
            step="0.01"
            min="0"
            label="Amount (₹)"
            value={expenseAmount}
            onChange={(e) => setExpenseAmount(e.target.value)}
            placeholder="e.g. 250"
            required
            className="text-xs"
          />
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Description</label>
            <Input
              value={expenseDescription}
              onChange={(e) => setExpenseDescription(e.target.value)}
              placeholder="e.g. Fuel, toll, parking"
              className="text-xs"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Receipt photo</label>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                loading={uploadingReceipt}
                onClick={() => expenseReceiptFileRef.current?.click()}
              >
                <Camera className="w-3.5 h-3.5" /> Take photo / upload
              </Button>
              {expenseReceiptUrl && (
                <span className="text-[11px] font-bold text-success-text flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Attached
                </span>
              )}
            </div>
            <input
              ref={expenseReceiptFileRef}
              type="file"
              accept="image/*,application/pdf"
              capture="environment"
              className="hidden"
              onChange={onExpenseReceiptFile}
            />
          </div>
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200">
            <Button type="button" variant="outline" size="sm" onClick={() => setIsExpenseModalOpen(false)} disabled={submittingExpense}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={submittingExpense}>
              {submittingExpense ? (
                <span className="flex items-center gap-1">
                  <Spinner size="sm" /> Submitting...
                </span>
              ) : (
                'Submit Claim'
              )}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Mark Completed Modal */}
      <Modal
        open={isCompleteModalOpen}
        onClose={() => {
          setIsCompleteModalOpen(false);
          stopScanner();
        }}
        title="Complete Product Delivery"
        size="md"
      >
        <form onSubmit={handleCompleteDelivery} className="space-y-3.5">
          <div className="p-3 rounded-lg bg-success/10 border border-success/25 text-xs text-success-text flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 text-success-text mt-0.5" />
            <div>
              <p className="font-bold">Confirmation of Cargo Handover</p>
              <p className="text-[11px] text-success-text mt-0.5">
                This will automatically mark document <b>{activeDelivery?.documentNumber}</b> as Delivered in ZYNX.
              </p>
            </div>
          </div>

          {/* QR delivery confirmation — only shown when this delivery actually
              carries a code (legacy deliveries have none and skip straight to
              the receiver details below). */}
          {activeDelivery?.deliveryCode && (
            <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-200 space-y-2.5">
              <div className="flex items-start gap-2 text-xs text-indigo-950">
                <QrCode className="w-4 h-4 text-indigo-700 mt-0.5 shrink-0" />
                <div>
                  <p className="font-bold">Scan the customer's QR code</p>
                  <p className="text-[11px] text-indigo-800 mt-0.5">
                    Sent to the customer when this delivery was dispatched. Delivery cannot be marked
                    complete until this is confirmed.
                  </p>
                </div>
              </div>

              {scannedCode ? (
                <div className="flex items-center justify-between gap-2 rounded-lg bg-white border border-success/25 px-3 py-2">
                  <span className="flex items-center gap-1.5 text-xs font-bold text-success-text">
                    <CheckCircle2 className="w-4 h-4" /> Code confirmed: {scannedCode}
                  </span>
                  <button
                    type="button"
                    onClick={() => setScannedCode('')}
                    className="text-[11px] font-semibold text-slate-500 hover:text-slate-800"
                  >
                    Rescan
                  </button>
                </div>
              ) : scannerOpen ? (
                <div className="space-y-2">
                  <div className="relative overflow-hidden rounded-lg border border-indigo-300 bg-black">
                    <video ref={videoRef} className="w-full aspect-square object-cover" muted playsInline />
                    <canvas ref={scanCanvasRef} className="hidden" />
                  </div>
                  <Button type="button" variant="outline" size="sm" className="w-full" onClick={stopScanner}>
                    <XIcon className="w-3.5 h-3.5" /> Cancel Scan
                  </Button>
                </div>
              ) : (
                <Button type="button" size="sm" className="w-full bg-indigo-700 hover:bg-indigo-800 text-white" onClick={startScanner}>
                  <Camera className="w-4 h-4" /> Open Camera to Scan
                </Button>
              )}

              {scannerError && <p className="text-[11px] text-danger-text">{scannerError}</p>}

              {!scannedCode && (
                <div>
                  <label className="block text-[11px] font-semibold text-indigo-900 mb-1">
                    Or type the code the customer was sent
                  </label>
                  <Input
                    value={manualCode}
                    onChange={(e) => setManualCode(e.target.value)}
                    placeholder="e.g. 482913"
                    className="text-xs font-mono"
                  />
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Receiver's Full Name <span className="text-danger-text">*</span>
            </label>
            <Input
              value={receiverName}
              onChange={(e) => setReceiverName(e.target.value)}
              placeholder="e.g. Ramesh Kumar (Store In-Charge)"
              required
              className="text-xs"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Receiver Contact Phone (Optional)
            </label>
            <Input
              value={receiverPhone}
              onChange={(e) => setReceiverPhone(e.target.value)}
              placeholder="e.g. 9822000000"
              className="text-xs font-mono"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Delivery Remarks / Unloading Notes
            </label>
            <textarea
              rows={2}
              value={proofRemarks}
              onChange={(e) => setProofRemarks(e.target.value)}
              placeholder="e.g., 7 Drums unloaded in Good Condition. Gate pass signed."
              className="w-full p-2.5 rounded-lg border border-slate-300 text-xs bg-white focus:ring-2 focus:ring-success/40 focus:outline-none resize-none"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setIsCompleteModalOpen(false);
                stopScanner();
              }}
              disabled={completing}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              className="bg-success-text hover:bg-success-text/90 text-on-primary"
              disabled={completing || Boolean(activeDelivery?.deliveryCode) && !(scannedCode || manualCode).trim()}
            >
              {completing ? (
                <span className="flex items-center gap-1">
                  <Spinner size="sm" /> Completing...
                </span>
              ) : (
                'Confirm & Complete Delivery'
              )}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
